import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useExportSymbols, useSearchLibraries } from '../../hooks/api/use-symbols.hook';
import { ExportSymbolForm } from './ExportSymbolForm';

export const ExportSymbols = () => {
  const navigate = useNavigate();

  const [exportSymbols, { error: exportSymbolsError }] = useExportSymbols();
  const { loading: loadingLibraries, response: librariesResponse, error: librariesError } = useSearchLibraries({});

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ExportSymbolForm>({ resolver: classValidatorResolver(ExportSymbolForm) });

  const onSubmit: SubmitHandler<ExportSymbolForm> = async ({ library }) => {
    await exportSymbols({ libraryId: library.id });
    navigate('..');
  };

  const renderContent = () => {
    if (librariesError) {
      return <Alert severity="error">{librariesError.response?.data.message}</Alert>;
    }

    return (
      <>
        <Controller
          control={control}
          name="library"
          render={({ field }) => (
            <Autocomplete
              {...register('library')}
              loading={loadingLibraries}
              sx={{ mt: '1rem' }}
              onChange={(_event, option) => field.onChange(option)}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={({ name }) => name}
              options={librariesResponse?.pages.map(({ result }) => result).flat() ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Library"
                  error={Boolean(errors.library)}
                  helperText={errors.library?.message}
                />
              )}
            />
          )}
        />
      </>
    );
  };

  return (
    <Dialog open={true}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>Export Symbols / Lines</DialogTitle>
        <DialogContent>
          {renderContent()}
          {exportSymbolsError ? (
            <Alert sx={{ mt: '1rem' }} severity="error">
              {exportSymbolsError.response?.data?.message}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
            Cancel
          </Button>
          <LoadingButton color="primary" type="submit" variant="contained" loading={isSubmitting}>
            Export
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
